import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import TimerOutlinedIcon from "@mui/icons-material/TimerOutlined";
import { useTranslation } from "react-i18next";
import { DetailSection } from "../../../../components/ui/detailView";
import { formatDateTimeInline } from "../../../../utils";
import { JobStatusPeriod, JobStatusTimeline } from "../../../../types/jobs";

const IN_PROGRESS = "IP";

// Segment colours per status. The hue families follow the status chips
// (JOB_STATUS_COLOR_MAP: primary / info / error), re-stepped because the chips'
// primary and info blues are nearly identical - fine on a labelled chip, but
// unreadable as two touching segments, and IN_PROGRESS -> FOR_VERIFICATION is
// the one transition every job makes. Each set is checked for colour-blind
// separation against its mode's paper surface.
const SEGMENT_COLORS: Record<"light" | "dark", Record<string, string>> = {
  light: { IP: "#1976d2", FV: "#29b6f6", FI: "#d32f2f" },
  dark: { IP: "#1565c0", FV: "#0097a7", FI: "#f44336" },
};

const MINUTE = 60;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;

/** A swatch beside a status name - identity rides on it, never on the text. */
const StatusSwatch = ({ color }: { color: string }) => (
  <Box
    component="span"
    sx={{ width: 10, height: 10, borderRadius: "3px", bgcolor: color, flexShrink: 0, display: "inline-block" }}
  />
);

type JobStatusDurationsProps = {
  timeline?: JobStatusTimeline;
};

/**
 * How long the job spent in each status since it was picked up - a total per
 * status, the periods on one proportional strip, and the same periods as a
 * table. PENDING and FINISHED are left out by the backend.
 */
export const JobStatusDurations = ({ timeline }: JobStatusDurationsProps) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const colors = SEGMENT_COLORS[theme.palette.mode];
  const periods = timeline?.periods ?? [];
  const totals = timeline?.totals ?? [];
  const lastPeriod = periods[periods.length - 1];
  // Null once the job is finished - its last timed period then has an end.
  const ongoingStatus = lastPeriod?.ended_at === null ? lastPeriod.status : null;

  // The two largest units are enough to compare stretches at a glance:
  // "2 d 3 h", "3 h 15 min", "12 min".
  const formatDuration = (seconds: number): string => {
    if (seconds < MINUTE) {
      return t("< 1 min");
    }
    const days = Math.floor(seconds / DAY);
    const hours = Math.floor((seconds % DAY) / HOUR);
    const minutes = Math.floor((seconds % HOUR) / MINUTE);
    const parts =
      days > 0
        ? [days > 0 && t("{{value}} d", { value: days }), hours > 0 && t("{{value}} h", { value: hours })]
        : [hours > 0 && t("{{value}} h", { value: hours }), minutes > 0 && t("{{value}} min", { value: minutes })];
    return parts.filter(Boolean).join(" ");
  };

  const periodRange = (period: JobStatusPeriod) =>
    `${formatDateTimeInline(period.started_at)} – ${
      period.ended_at ? formatDateTimeInline(period.ended_at) : t("now")
    }`;

  return (
    <DetailSection title={t("Time in statuses")} icon={<TimerOutlinedIcon fontSize="small" />} padded>
      {periods.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ fontStyle: "italic" }}>
          {t("The job has not been started yet, or its status changes were not recorded.")}
        </Typography>
      ) : (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
              gap: 1.5,
            }}
          >
            {totals.map((total) => (
                <Box
                  key={total.status}
                  sx={{
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: "8px",
                    px: 1.5,
                    py: 1.25,
                    display: "flex",
                    flexDirection: "column",
                    gap: 0.25,
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                    <StatusSwatch color={colors[total.status]} />
                    <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
                      {total.status_display}
                    </Typography>
                  </Box>
                  <Typography variant="h6" component="div" sx={{ fontWeight: 600, lineHeight: 1.3 }}>
                    {formatDuration(total.duration_seconds)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {[
                      // The stretch the client watches most: from picking the
                      // job up until handing it over for verification.
                      total.status === IN_PROGRESS && t("Until sent for verification"),
                      total.periods_count > 1 && t("{{value}} times", { value: total.periods_count }),
                      total.status === ongoingStatus && t("ongoing"),
                    ]
                      .filter(Boolean)
                      .join(" · ") || " "}
                  </Typography>
                </Box>
            ))}
          </Box>

          <Box>
            <Box
              role="img"
              aria-label={t("Status timeline")}
              sx={{ display: "flex", gap: "2px", alignItems: "center" }}
            >
              {periods.map((period, index) => (
                <Tooltip
                  key={`${period.status}-${period.started_at}`}
                  arrow
                  title={
                    <>
                      <Box sx={{ fontWeight: 600 }}>
                        {`${period.status_display}: ${formatDuration(period.duration_seconds)}`}
                      </Box>
                      <Box>{periodRange(period)}</Box>
                    </>
                  }
                >
                  {/* The hit target is taller than the bar it holds. */}
                  <Box
                    sx={{
                      flex: `${Math.max(period.duration_seconds, 1)} 1 0`,
                      minWidth: 6,
                      py: 0.75,
                      cursor: "default",
                    }}
                  >
                    <Box
                      sx={{
                        height: 14,
                        bgcolor: colors[period.status],
                        borderTopLeftRadius: index === 0 ? 4 : 0,
                        borderBottomLeftRadius: index === 0 ? 4 : 0,
                        borderTopRightRadius: index === periods.length - 1 ? 4 : 0,
                        borderBottomRightRadius: index === periods.length - 1 ? 4 : 0,
                      }}
                    />
                  </Box>
                </Tooltip>
              ))}
            </Box>
            <Box sx={{ display: "flex", justifyContent: "space-between", gap: 2 }}>
              <Typography variant="caption" color="text.secondary">
                {formatDateTimeInline(periods[0].started_at)}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {lastPeriod.ended_at ? formatDateTimeInline(lastPeriod.ended_at) : t("now")}
              </Typography>
            </Box>
          </Box>

          <Box sx={{ border: "1px solid", borderColor: "divider", borderRadius: 1, overflow: "hidden" }}>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>{t("Status")}</TableCell>
                    <TableCell>{t("From")}</TableCell>
                    <TableCell>{t("To")}</TableCell>
                    <TableCell align="right">{t("Duration")}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {periods.map((period) => (
                    <TableRow
                      key={`${period.status}-${period.started_at}`}
                      sx={{ "&:last-child td": { borderBottom: 0 } }}
                    >
                      <TableCell>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                          <StatusSwatch color={colors[period.status]} />
                          {period.status_display}
                        </Box>
                      </TableCell>
                      <TableCell>{formatDateTimeInline(period.started_at)}</TableCell>
                      <TableCell>
                        {period.ended_at ? (
                          formatDateTimeInline(period.ended_at)
                        ) : (
                          <Typography variant="body2" component="span" color="text.secondary" sx={{ fontStyle: "italic" }}>
                            {t("ongoing")}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell align="right" sx={{ fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" }}>
                        {formatDuration(period.duration_seconds)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        </Box>
      )}
    </DetailSection>
  );
};
